import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card_Widget';
import { BarChart } from '@/components/charts';
import { useAuthV2 } from '@/context/auth-v2/AuthContext';
import { useNavigate } from 'react-router-dom';
import { StarIcon } from '@heroicons/react/24/solid';
import Button from '@/components/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { mockSocialImpactStatsBusiness } from '@/utils/mockData';

interface SocialImpactStats {
  totalDonations: number;
  totalEvents: number;
  totalVolunteers: number;
  totalItems: number;
  totalValueDonated: number;
  uniqueCharities: number;
  impactMetrics: {
    environmental: number;
    social: number;
    economic: number;
  };
  recentActivities: Array<{
    id: string;
    type: 'donation' | 'event' | 'volunteer';
    title: string;
    impact: number;
    date: string;
  }>;
}

interface Donation {
  id: string;
  environmental_impact: number;
  social_impact: number;
  economic_impact: number;
  amount: number;
  charity_name: string;
  charity_id: string;
  created_at: string;
  status: 'pending' | 'confirmed' | 'rejected';
  items: {
    id: string;
    name: string;
    quantity: number;
    price: number;
  }[];
  reviewed?: boolean;
}

interface DashboardEvent {
  id: string;
  name: string;
  impact_score: number;
  date: string;
  volunteer_count: number;
}

export default function SocialImpactDashboard() {
  const { user } = useAuthV2();
  const navigate = useNavigate();
  const [stats, setStats] = useState<SocialImpactStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [, setDonations] = useState<Donation[]>([]);
  const [confirmedDonations, setConfirmedDonations] = useState<Donation[]>([]);

  useEffect(() => {
    fetchSocialImpactStats();
  }, [user]);

  const fetchSocialImpactStats = async () => {
    setLoading(true);
    try {
      // Get current user with normalizeUser
      const { createClient, normalizeUser } = await import('@/services/supabase');
      const supabase = createClient();
      const { data: { session } } = await (await supabase).auth.getSession();
      
      // Check if we have a valid session
      if (!session?.user) {
        console.error('No valid Supabase session found');
        setLoading(false);
        return;
      }
      
      // Use normalizeUser to get consistent user data
      const normalizedUser = normalizeUser(session.user);
      const isSuperAdmin = normalizedUser.role === 'super_admin';
      
      if (isSuperAdmin) {
        // For super admin, use mock data
        console.log('Using mock social impact data for super admin');
        
        // Short timeout to simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Set mock stats - need to type-cast to ensure compatibility
        const mockStats = mockSocialImpactStatsBusiness as SocialImpactStats;
        
        // Calculate total items donated from the mock donations
        const totalDonatedItems = mockStats.recentActivities
          .filter(activity => activity.type === 'donation')
          .reduce((total, activity) => total + activity.impact, 0);
            
        // Update the total items count
        mockStats.totalItems = totalDonatedItems;
        
        // Set the updated stats
        setStats(mockStats);
        
        // Create mock donations for the donations table
        const mockDonations: Donation[] = [
          {
            id: 'md1-f9b0-4cd8-a55a-12345678f001',
            environmental_impact: 120,
            social_impact: 90,
            economic_impact: 70,
            amount: 850.50,
            charity_name: 'Jamaica Marine Conservation',
            charity_id: '9d4f08e8-f9b0-4cd8-a55a-49435583f101',
            created_at: '2025-05-10T14:30:00Z',
            status: 'confirmed',
            items: [
              { id: 'i1', name: 'Fresh Red Snapper', quantity: 15, price: 12.99 },
              { id: 'i2', name: 'Lobster', quantity: 5, price: 19.99 }
            ],
            reviewed: true
          },
          {
            id: 'md2-f9b0-4cd8-a55a-12345678f002',
            environmental_impact: 80,
            social_impact: 110,
            economic_impact: 60,
            amount: 720.25,
            charity_name: 'Fisher Folk Support Network',
            charity_id: '7f6f08e8-f9b0-4cd8-a55a-49435583f103',
            created_at: '2025-05-05T11:45:00Z',
            status: 'confirmed',
            items: [
              { id: 'i3', name: 'Tilapia', quantity: 20, price: 8.50 },
              { id: 'i4', name: 'Cod', quantity: 10, price: 14.75 }
            ],
            reviewed: false
          },
          {
            id: 'md3-f9b0-4cd8-a55a-12345678f003',
            environmental_impact: 95,
            social_impact: 70,
            economic_impact: 85,
            amount: 650.00,
            charity_name: 'Feed the Coast',
            charity_id: '8e5f08e8-f9b0-4cd8-a55a-49435583f102',
            created_at: '2025-04-29T11:20:00Z',
            status: 'confirmed',
            items: [
              { id: 'i5', name: 'Salmon', quantity: 12, price: 16.99 },
              { id: 'i6', name: 'Shrimp', quantity: 8, price: 21.50 }
            ],
            reviewed: true
          }
        ];
        
        setDonations(mockDonations);
        setConfirmedDonations(mockDonations.filter(d => d.status === 'confirmed'));
      } else {
        // For regular users, use real API
        // Supabase client is already created above
        
        // Fetch donations using normalized user business_id
        const { data: donationsData, error: donationsError } = await (await supabase)
          .from('donations')
          .select('*, items(*)')
          .eq('business_id', normalizedUser.business_id);

        if (donationsError) throw donationsError;
        
        // Add mock data indicating whether a donation has been reviewed
        const enhancedDonations = donationsData.map((donation: Donation) => ({
          ...donation,
          reviewed: Math.random() > 0.5 // Mock data - some donations are reviewed, some aren't
        }));
        
        setDonations(enhancedDonations);
        
        // Set confirmed donations
        const confirmed = enhancedDonations.filter((d: Donation) => d.status === 'confirmed');
        setConfirmedDonations(confirmed);

        // Fetch events using the same Supabase client with normalized user business_id
        const { data: events, error: eventsError } = await (await supabase)
          .from('events')
          .select('*')
          .eq('business_id', normalizedUser.business_id);

        if (eventsError) throw eventsError;

        // Calculate impact metrics with proper types
        const environmental = enhancedDonations.reduce((acc: number, curr: Donation) => acc + (curr.environmental_impact || 0), 0);
        const social = enhancedDonations.reduce((acc: number, curr: Donation) => acc + (curr.social_impact || 0), 0);
        const economic = enhancedDonations.reduce((acc: number, curr: Donation) => acc + (curr.economic_impact || 0), 0);

        // Combine recent activities
        // Create recent activities array from donations and events
        const recentActivities = [
          ...enhancedDonations.map((d: Donation) => ({
            id: d.id,
            type: 'donation' as 'donation' | 'event' | 'volunteer',
            title: `Donation to ${d.charity_name}`,
            impact: d.amount,
            date: d.created_at,
          })),
          ...events.map((e: DashboardEvent) => ({
            id: e.id,
            type: 'event' as 'donation' | 'event' | 'volunteer',
            title: e.name,
            impact: e.impact_score,
            date: e.date,
          })),
        ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
         .slice(0, 5);

        // Calculate donation statistics
        const totalItems = enhancedDonations.reduce((acc, donation) => {
          return acc + donation.items.reduce((itemAcc, item) => itemAcc + item.quantity, 0);
        }, 0);

        const totalValueDonated = enhancedDonations.reduce((acc, donation) => {
          return acc + donation.amount;
        }, 0);

        // Get unique charities
        const uniqueCharityIds = new Set(enhancedDonations.map(d => d.charity_id));

        setStats({
          totalDonations: enhancedDonations.length,
          totalEvents: events.length,
          totalVolunteers: events.reduce((acc, e) => acc + e.volunteer_count, 0),
          totalItems,
          totalValueDonated,
          uniqueCharities: uniqueCharityIds.size,
          impactMetrics: {
            environmental,
            social,
            economic,
          },
          recentActivities,
        });
      }
    } catch (error) {
      console.error('Error fetching social impact stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (!stats) return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <h1 className="text-xl font-medium text-navy-blue">Social Impact Dashboard</h1>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md text-center">
          <p className="text-gray-600">No data available</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center mb-6">
          <button
            type="button"
            onClick={() => navigate('/business/dashboard')}
            className="mr-4 text-gray-500 hover:text-navy-blue"
          >
          
          </button>
          <h1 className="text-xl font-medium text-navy-blue">Social Impact Dashboard</h1>
        </div>
        
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="donations">Donations</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview">
      
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
          <CardHeader>
            <CardTitle>Total Donations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">{stats.totalDonations}</div>
            <div className="text-sm text-gray-500">donations made</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Total Events</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">{stats.totalEvents}</div>
            <div className="text-sm text-gray-500">events hosted</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Total Volunteers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">{stats.totalVolunteers}</div>
            <div className="text-sm text-gray-500">volunteers engaged</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Total Impact Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">
              {stats.totalItems}
            </div>
            <div className="text-sm text-gray-500">items donated</div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Impact Distribution by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <BarChart
              data={[
                { name: 'Environmental', value: stats.impactMetrics.environmental },
                { name: 'Social', value: stats.impactMetrics.social },
                { name: 'Economic', value: stats.impactMetrics.economic },
              ]}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Activities</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats.recentActivities.map((activity) => (
                <div key={activity.id} className="flex justify-between items-center">
                  <div>
                    <div className="font-semibold">{activity.title}</div>
                    <div className="text-sm text-gray-500">
                      {new Date(activity.date).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="text-green-600 font-semibold">
                    {activity.type === 'donation' ? `${activity.impact} items` : `${activity.impact} impact`}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        </div>
          </TabsContent>
          
          <TabsContent value="donations">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle>Total Donations</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold">{stats.totalDonations}</div>
                  <div className="text-sm text-gray-500">donations made</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Total Items</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold">{stats.totalItems}</div>
                  <div className="text-sm text-gray-500">items donated</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Total Value</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold">${stats.totalValueDonated.toFixed(2)}</div>
                  <div className="text-sm text-gray-500">value of donations</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Charities Helped</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold">{stats.uniqueCharities}</div>
                  <div className="text-sm text-gray-500">unique recipients</div>
                </CardContent>
              </Card>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle>Confirmed Donations</CardTitle>
                <div className="text-sm text-gray-500">You can rate charities for completed donations</div>
              </CardHeader>
              <CardContent>
                {confirmedDonations.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">No confirmed donations yet</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Charity</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {confirmedDonations.map((donation) => {
                          const totalItems = donation.items.reduce((acc, item) => acc + item.quantity, 0);
                          const totalValue = donation.items.reduce((acc, item) => acc + (item.price * item.quantity), 0);
                          
                          return (
                            <tr key={donation.id}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="font-medium text-gray-900">{donation.charity_name}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-gray-900">{totalItems} items</div>
                                <div className="text-sm text-gray-500">
                                  {donation.items.map(item => `${item.quantity}x ${item.name}`).join(', ')}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-gray-900">${totalValue.toFixed(2)}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-gray-900">{new Date(donation.created_at).toLocaleDateString()}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {donation.reviewed ? (
                                  <div className="flex items-center text-gray-500">
                                    <StarIcon className="h-5 w-5 text-yellow-400 mr-1" />
                                    <span>Reviewed</span>
                                  </div>
                                ) : (
                                  <Button 
                                    onClick={() => navigate(`/statistics/charity-rating/${donation.charity_id}?donationId=${donation.id}`)}
                                    className="bg-green-500 hover:bg-green-600"
                                  >
                                    Rate Charity
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
    </div>
  );
}